import { ComponentProps } from 'react';
import {
  Link,
  generatePath,
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';

import { Select } from '@fly/core';
import { relativeRoutes, routes } from '@fly/shared';
import { BreadcrumbComponent } from '@fly/shared-ui';
import { Link as RadixLink } from '@radix-ui/themes';

import { useGetRepositoryNamesQuery } from '../../store/registry.service';

const tabRoutePatterns: Record<string, string> = {
  [relativeRoutes.ARTIFACTS]: routes.DASHBOARD.REGISTRY.REPOSITORY.ARTIFACTS,
  [relativeRoutes.WORKFLOWS]: routes.DASHBOARD.REGISTRY.REPOSITORY.WORKFLOWS,
  [relativeRoutes.RELEASES]: routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES,
  [relativeRoutes.DECISION_RECORDS]:
    routes.DASHBOARD.REGISTRY.REPOSITORY.DECISION_RECORDS,
};

export const ArtifactsBreadcrumb: BreadcrumbComponent = ({
  isLast,
}: ComponentProps<BreadcrumbComponent>) => {
  const { owner, name } = useParams() as { owner: string; name: string };
  const { pathname, state } = useLocation();
  const from = state?.from as string | undefined;

  const navigate = useNavigate();

  const { data: repositoryNames = [] } = useGetRepositoryNamesQuery();

  let to = generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES, {
    owner,
    name,
  });

  if (
    matchPath(routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASE_ARTIFACTS, pathname)
  ) {
    to = generatePath(routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES, {
      owner,
      name,
    });
  }

  const title = `${owner}/${name}`;

  const navigateToRepo = (repoName: string) => {
    const currentTab = pathname.split('/').pop() || '';
    const routePattern =
      tabRoutePatterns[currentTab] ??
      routes.DASHBOARD.REGISTRY.REPOSITORY.RELEASES;

    const path = generatePath(routePattern, {
      owner,
      name: repoName,
    });
    navigate(path);
  };

  return isLast ? (
    <RadixLink className='text-fly-general-black'>
      {repositoryNames.length > 1 ? (
        <Select
          variant='ghost'
          color='neutral'
          size='1'
          className='text-size-fly-3'
          onValueChange={navigateToRepo}
          value={name}
          options={repositoryNames.map((repository) => ({
            value: repository.name,
            textValue: `${owner}/${repository.name}`,
          }))}
        />
      ) : (
        <strong>{title}</strong>
      )}
    </RadixLink>
  ) : (
    <RadixLink asChild color='gray'>
      <Link className='font-semibold' to={from || to}>
        {title}
      </Link>
    </RadixLink>
  );
};
